import type { ProductProfileConfig } from "@/lib/api/tenant-settings";

export type ProductNavItemId =
  | "matters"
  | "review"
  | "documents"
  | "asks"
  | "search"
  | "exports"
  | "queue"
  | "help"
  | "portfolio"
  | "my-work"
  | "case-tools"
  | "clustering"
  | "review-center"
  | "assistant"
  | "redactions";

export type ProductRouteKey =
  | "matters"
  | "documents"
  | "search"
  | "exports"
  | "queue"
  | "help"
  | "portfolio"
  | "my-work"
  | "case-tools"
  | "clustering"
  | "review-center"
  | "assistant"
  | "redactions"
  | "chronology"
  | "review"
  | "preflight"
  | "unlock"
  | "sweep";

export type ProductWorkflowStepId =
  | "strategy"
  | "evidence"
  | "chronology"
  | "review"
  | "production"
  | "case-tools";

export const defaultProductProfile: ProductProfileConfig = {
  id: "eca_accelerator",
  label: "Cull & Handoff",
  description: "Upload evidence, narrow the review set, build a cited timeline, and hand off a smaller defensible package.",
  public_home_mode: "marketing",
  marketing: {
    hero: "Upload evidence, narrow the review set, build a cited timeline, and hand off the package before hosted-review meters start running.",
    subhead:
      "Preflight cost and risk, narrow the review population, run QC and privilege checks, build a cited timeline, and export a defensible handoff pack.",
    primary_cta: "Start a pilot",
    secondary_cta: "See sample handoff pack",
    proof_points: [
      "Preflight cost and risk scan",
      "Review queues with explainable QC and privilege controls",
      "Cited timeline plus export-ready defensibility packet",
    ],
  },
  pricing: {
    headline: "Choose the rollout that matches the matter",
    subhead:
      "Commercial terms are still being finalized, but the two supported motions are already clear.",
    plans: [
      {
        id: "lite-standalone",
        label: "Lite standalone",
        price_label: "Pilot scope",
        summary: "One focused matter worked inside ECA from evidence through handoff.",
        cta_label: "Plan pilot",
      },
      {
        id: "pre-review-add-on",
        label: "Pre-review add-on",
        price_label: "Scoped rollout",
        summary: "Reduce what reaches Relativity, Everlaw, or DISCO and hand off a smaller package.",
        cta_label: "Scope rollout",
      },
    ],
    footnote:
      "Packaging is provisional; the workflow contract is not.",
  },
  nav: {
    default_home_route: "/matters",
    primary_items: ["matters", "search", "review", "exports", "help"],
    hidden_items: [
      "documents",
      "queue",
      "portfolio",
      "my-work",
      "case-tools",
      "clustering",
      "review-center",
      "assistant",
      "redactions",
    ],
  },
  workflow: {
    show_preflight: true,
    show_unlock: true,
    show_search: true,
    show_sweep: true,
    show_chronology: true,
    show_review: true,
    show_exports: true,
    show_queue: true,
    show_help: true,
    show_assistant: true,
    show_portfolio: false,
    show_my_work: false,
    show_case_tools: true,
    show_clustering: false,
    show_review_center: false,
    show_redactions: true,
  },
};

/**
 * Lite profile - focused front-stage for solo attorneys and small firms.
 *
 * Global nav: Matters, Help (Admin role-gated only)
 * Matter tabs: Home, Collection, Search, Timeline, Production, Settings
 * Hidden: Review, Queue, Portfolio, My Work, Case Tools, Clustering, etc.
 */
export const liteProductProfile: ProductProfileConfig = {
  id: "eca_lite",
  label: "Lite",
  description: "Focused matter workflow for small teams that need collection, search, review, timeline, and handoff in one place.",
  public_home_mode: "marketing",
  marketing: {
    hero: "Upload evidence, narrow the review set, build a cited timeline, and hand off a defensible package.",
    subhead: "One focused workflow from collection through review and production.",
    primary_cta: "Start a matter",
    proof_points: [
      "Upload and process any document format",
      "AI-powered search with citations",
      "Review and QC the reduced set before handoff",
      "Build cited timelines from search results",
      "Export defensible production packages",
    ],
  },
  pricing: {
    headline: "Pilot packaging is scoped with you",
    subhead: "Use this workspace to validate the workflow first; commercial terms stay flexible while packaging is in progress.",
    plans: [
      {
        id: "pilot",
        label: "Pilot",
        price_label: "Scoped pilot",
        summary: "One matter, full workflow, with room to tune the rollout before broader adoption.",
        cta_label: "Get started",
      },
    ],
    footnote: "Pricing is intentionally provisional while the workflow and proof surfaces are still being finalized.",
  },
  nav: {
    default_home_route: "/matters",
    primary_items: ["matters", "help"],
    hidden_items: [
      "documents",
      "search",
      "review",
      "exports",
      "queue",
      "portfolio",
      "my-work",
      "case-tools",
      "clustering",
      "review-center",
      "redactions",
    ],
  },
  workflow: {
    show_preflight: true,
    show_unlock: true,
    show_search: true,
    show_sweep: false,
    show_chronology: true,
    show_review: true,
    show_exports: true,
    show_queue: false,
    show_help: true,
    show_assistant: true,
    show_portfolio: false,
    show_my_work: false,
    show_case_tools: false,
    show_clustering: false,
    show_review_center: false,
    show_redactions: false,
  },
};

export function resolveProductProfile(
  profile?: ProductProfileConfig | null
): ProductProfileConfig {
  return profile ?? defaultProductProfile;
}

export function isNavItemVisible(
  profile: ProductProfileConfig,
  item: ProductNavItemId
): boolean {
  if (profile.nav.hidden_items.includes(item)) return false;
  if (profile.nav.primary_items.includes(item)) return true;

  switch (item) {
    case "review":
      return profile.workflow.show_review;
    case "portfolio":
      return profile.workflow.show_portfolio;
    case "my-work":
      return profile.workflow.show_my_work;
    case "case-tools":
      return profile.workflow.show_case_tools;
    case "clustering":
      return profile.workflow.show_clustering;
    case "review-center":
      return profile.workflow.show_review_center;
    case "assistant":
      return profile.workflow.show_assistant;
    case "redactions":
      return profile.workflow.show_redactions;
    default:
      return true;
  }
}

export function isRouteEnabled(
  profile: ProductProfileConfig,
  route: ProductRouteKey
): boolean {
  switch (route) {
    case "portfolio":
      return profile.workflow.show_portfolio;
    case "my-work":
      return profile.workflow.show_my_work;
    case "case-tools":
      return profile.workflow.show_case_tools;
    case "clustering":
      return profile.workflow.show_clustering;
    case "review-center":
      return profile.workflow.show_review_center;
    case "assistant":
      return profile.workflow.show_assistant;
    case "redactions":
      return profile.workflow.show_redactions;
    case "preflight":
      return profile.workflow.show_preflight;
    case "unlock":
      return profile.workflow.show_unlock;
    case "sweep":
      return profile.workflow.show_sweep;
    case "chronology":
      return profile.workflow.show_chronology;
    case "review":
      return profile.workflow.show_review;
    case "exports":
      return profile.workflow.show_exports;
    case "queue":
      return profile.workflow.show_queue;
    case "help":
      return profile.workflow.show_help;
    default:
      return true;
  }
}

export function isWorkflowStepVisible(
  profile: ProductProfileConfig,
  stepId: ProductWorkflowStepId
): boolean {
  switch (stepId) {
    case "strategy":
      return profile.workflow.show_preflight || profile.workflow.show_unlock;
    case "chronology":
      return profile.workflow.show_chronology;
    case "review":
      return profile.workflow.show_review;
    case "production":
      return profile.workflow.show_exports;
    case "case-tools":
      return false;
    case "evidence":
    default:
      return true;
  }
}

export function getVisibleWorkflowStepIds(
  profile: ProductProfileConfig
): ProductWorkflowStepId[] {
  const orderedSteps: ProductWorkflowStepId[] = [
    "strategy",
    "evidence",
    "chronology",
    "review",
    "production",
    "case-tools",
  ];

  return orderedSteps.filter((stepId) => isWorkflowStepVisible(profile, stepId));
}
